// MARCA | 11/05/2026 | New file – Cart gift box prompt, nested display, and orphan cleanup
import { ThemeEvents, CartAddEvent } from '@theme/events';

var addInProgress = false;
var removeInProgress = false;
var cleanupInProgress = false;

// MARCA | 14/05/2026 | Add gift box from cart prompt. If parent qty > 1, split into two lines:
// one with gift box (new line with _upsell_group) and one without (original, qty reduced by 1).
document.addEventListener('click', async function (e) {
  var button = /** @type {HTMLButtonElement | null} */ (
    /** @type {HTMLElement} */ (e.target).closest('.cart-giftbox-prompt__button')
  );
  if (!button || button.disabled || addInProgress) return;

  addInProgress = true;
  var textEl = button.querySelector('.cart-giftbox-prompt__button-text');
  var originalText = textEl ? textEl.textContent : '';
  button.disabled = true;
  if (textEl) textEl.textContent = 'Adding...';

  try {
    var prompt = /** @type {HTMLElement | null} */ (
      button.closest(".cart-giftbox-prompt")
    );
    var giftboxVariantId = parseInt(prompt?.dataset?.variantId || "0", 10);
    var productTitle = prompt?.dataset?.productTitle || "";
    var parentVariantId = parseInt(prompt?.dataset?.parentVariantId || "0", 10);
    var parentKey = prompt?.dataset?.parentKey || "";
    var parentQty = parseInt(prompt?.dataset?.parentQuantity || "1", 10);
    var existingGroup = prompt?.dataset?.upsellGroup || "";
    var routes = /** @type {any} */ (window.Shopify)?.routes?.root || "/";

    if (parentQty > 1) {
      var groupId = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

      /** @type {Array<{id: number, quantity: number, properties?: Record<string, string>}>} */
      var items = [
        { id: parentVariantId, quantity: 1, properties: { "_upsell_group": groupId } },
        { id: giftboxVariantId, quantity: 1, properties: { "Gift wrap for": productTitle, "_upsell_group": groupId } },
      ];
      var addRes = await fetch(routes + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: items }),
      });
      if (!addRes.ok) throw new Error("Batch add failed: " + addRes.status);

      /** @type {Record<string, number>} */
      var updates = {};
      updates[parentKey] = parentQty - 1;
      var updateRes = await fetch(routes + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates: updates }),
      });
      if (!updateRes.ok) throw new Error("Quantity reduce failed: " + updateRes.status);
    } else {
      /** @type {Record<string, string>} */
      var props = { "Gift wrap for": productTitle };
      if (existingGroup) props["_upsell_group"] = existingGroup;

      var res = await fetch(routes + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          items: [{ id: giftboxVariantId, quantity: 1, properties: props }],
        }),
      });
      if (!res.ok) throw new Error("Add failed: " + res.status);
    }

    var cartRes = await fetch(routes + "cart.js");
    var cartData = await cartRes.json();

    document.dispatchEvent(
      new CartAddEvent(cartData, "cart-giftbox", {
        itemCount: cartData.item_count,
        source: "cart-giftbox",
      }),
    );
  } catch (err) {
    console.error('[CartGiftbox] Add to cart failed:', err);
    button.disabled = false;
    if (textEl) textEl.textContent = originalText || '';
  } finally {
    addInProgress = false;
  }
});

// MARCA | 11/05/2026 | Remove a nested gift box from cart
document.addEventListener('click', async function (e) {
  var button = /** @type {HTMLButtonElement | null} */ (
    /** @type {HTMLElement} */ (e.target).closest('.cart-giftbox-nested__remove')
  );
  if (!button || button.disabled || removeInProgress) return;

  removeInProgress = true;
  button.disabled = true;

  try {
    var nested = /** @type {HTMLElement | null} */ (button.closest('.cart-giftbox-nested'));
    var itemKey = nested?.dataset?.itemKey || '';
    var routes = /** @type {any} */ (window.Shopify)?.routes?.root || '/';

    var res = await fetch(routes + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: 0 }),
    });
    if (!res.ok) throw new Error('Remove failed: ' + res.status);

    var cartRes = await fetch(routes + 'cart.js');
    var cartData = await cartRes.json();

    document.dispatchEvent(
      new CartAddEvent(cartData, 'cart-giftbox', {
        itemCount: cartData.item_count,
        source: 'cart-giftbox',
      })
    );
  } catch (err) {
    console.error('[CartGiftbox] Remove failed:', err);
    button.disabled = false;
  } finally {
    removeInProgress = false;
  }
});

// MARCA | 14/05/2026 | Shared helpers for upsell context detection and atomic cart operations

var upsellActionInProgress = false;

/**
 * Resolve upsell context for a button inside a cart row. Checks the server-rendered
 * data-upsell-bundle attribute first, then falls back to a DOM walk that
 * detects nested gift-box / gift-card elements in the same table row.
 * @param {HTMLElement} button
 * @returns {{ row: HTMLElement, itemKey: string, variantId: number, isGrouped: boolean } | null}
 */
function resolveUpsellContext(button) {
  var wrapper = /** @type {HTMLElement | null} */ (
    button.closest("[data-upsell-bundle]")
  );
  if (wrapper) {
    var wrapperRow = /** @type {HTMLElement | null} */ (
      wrapper.closest(".cart-items__table-row")
    );
    return {
      row: wrapperRow || wrapper,
      itemKey: wrapper.dataset.upsellItemKey || "",
      variantId: parseInt(wrapper.dataset.upsellVariantId || "0", 10),
      isGrouped: wrapper.hasAttribute("data-upsell-grouped"),
    };
  }

  var row = /** @type {HTMLElement | null} */ (
    button.closest(".cart-items__table-row")
  );
  if (!row) return null;

  var hasNested = row.querySelector(
    ".cart-giftbox-nested, .cart-giftcard-nested",
  );
  var hasUpsellAttr = row.hasAttribute("data-has-upsell");
  if (!hasNested && !hasUpsellAttr) return null;

  var itemKey = row.dataset.key || "";
  var selector = /** @type {HTMLElement | null} */ (
    row.querySelector(".quantity-selector[data-variant-id]")
  );
  var variantId = parseInt(selector?.dataset?.variantId || "0", 10);
  return { row: row, itemKey: itemKey, variantId: variantId, isGrouped: false };
}

/**
 * Collect all nested upsell item keys from a cart row.
 * @param {HTMLElement} row
 * @returns {string[]}
 */
function collectNestedUpsellKeys(row) {
  var keys = [];
  var nested = row.querySelectorAll('.cart-giftbox-nested[data-item-key], .cart-giftcard-nested[data-item-key]');
  for (var i = 0; i < nested.length; i++) {
    var key = /** @type {HTMLElement} */ (nested[i]).dataset.itemKey;
    if (key) keys.push(key);
  }
  return keys;
}

/**
 * Atomically remove a parent item and all its nested upsells in a single /cart/update.js call.
 * @param {string} parentKey
 * @param {string[]} upsellKeys
 * @returns {Promise<void>}
 */
function atomicBundleRemove(parentKey, upsellKeys) {
  var routes = /** @type {any} */ (window.Shopify)?.routes?.root || "/";

  /** @type {Record<string, number>} */
  var updates = {};
  updates[parentKey] = 0;
  for (var i = 0; i < upsellKeys.length; i++) {
    var uk = upsellKeys[i];
    if (uk) updates[uk] = 0;
  }

  return fetch(routes + "cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ updates: updates }),
  })
    .then(function (res) {
      if (!res.ok) throw new Error("Bundle remove failed: " + res.status);
      return fetch(routes + "cart.js");
    })
    .then(function (res) {
      return res.json();
    })
    .then(function (cartData) {
      document.dispatchEvent(
        new CartAddEvent(cartData, "cart-giftbox", {
          itemCount: cartData.item_count,
          source: "cart-giftbox",
        }),
      );
    });
}

// MARCA | 14/05/2026 | Capture-phase handler for the theme's remove button (.cart-items__remove).
// For items with nested upsells, intercept and do an atomic batch removal via /cart/update.js.
// For items without upsells, the event passes through to the theme's normal handler.
// MARCA | 19/10/2026 | Registered on window (not document) – the theme's on:click delegate is a document capture listener
// registered first, so a document listener couldn't stop it and both removals ran (risking the wrong line being removed).
window.addEventListener(
  "click",
  function (e) {
    var target = /** @type {HTMLElement} */ (e.target);
    var removeBtn = /** @type {HTMLButtonElement | null} */ (
      target.closest(".cart-items__remove")
    );
    if (!removeBtn || upsellActionInProgress) return;

    var row = /** @type {HTMLElement | null} */ (
      removeBtn.closest(".cart-items__table-row")
    );
    if (!row) return;

    var upsellKeys = collectNestedUpsellKeys(row);
    if (upsellKeys.length === 0) return;

    var parentKey = row.dataset.key || "";
    if (!parentKey) return;

    e.stopImmediatePropagation();
    e.preventDefault();

    upsellActionInProgress = true;

    atomicBundleRemove(parentKey, upsellKeys)
      .catch(function (err) {
        console.error("[CartGiftbox] Atomic bundle remove failed:", err);
      })
      .finally(function () {
        upsellActionInProgress = false;
      });
  },
  true,
);

// MARCA | 14/05/2026 | Override quantity controls for items with associated upsells:
// Grouped bundles: minus → atomic removal of parent + all upsells via /cart/update.js
// All upsell items: plus → adds a new plain instance of the same variant without upsell properties
// Ungrouped items with cart-prompt upsells: minus → standard theme behaviour (decrement)
// MARCA | 19/10/2026 | Registered on window so it runs before the theme's document-level on:click delegate (see remove handler above)
window.addEventListener(
  "click",
  function (e) {
    var target = /** @type {HTMLElement} */ (e.target);
    var button = /** @type {HTMLButtonElement | null} */ (
      target.closest(".quantity-minus, .quantity-plus")
    );
    if (!button || upsellActionInProgress) return;

    var ctx = resolveUpsellContext(button);
    if (!ctx) return;

    var isMinus = button.classList.contains("quantity-minus");

    if (isMinus && !ctx.isGrouped) return;

    e.stopImmediatePropagation();
    e.preventDefault();

    upsellActionInProgress = true;

    if (isMinus) {
      var upsellKeys = collectNestedUpsellKeys(ctx.row);
      atomicBundleRemove(ctx.itemKey, upsellKeys)
        .catch(function (err) {
          console.error("[CartGiftbox] Upsell bundle remove failed:", err);
        })
        .finally(function () {
          upsellActionInProgress = false;
        });
    } else {
      var routes = /** @type {any} */ (window.Shopify)?.routes?.root || "/";
      fetch(routes + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items: [{ id: ctx.variantId, quantity: 1 }] }),
      })
        .then(function () {
          return fetch(routes + "cart.js");
        })
        .then(function (r) {
          return r.json();
        })
        .then(function (cartData) {
          document.dispatchEvent(
            new CartAddEvent(cartData, "cart-giftbox", {
              itemCount: cartData.item_count,
              source: "cart-giftbox",
            }),
          );
        })
        .catch(function (err) {
          console.error("[CartGiftbox] Plain variant add failed:", err);
        })
        .finally(function () {
          upsellActionInProgress = false;
        });
    }
  },
  true,
);

// MARCA | 14/05/2026 | Orphan detection – scans cart data and returns keys of upsell items whose parent is missing.
// Used by both the event-based safety net and the page-load cleanup.
function detectOrphans() {
  var giftboxEl = /** @type {HTMLElement | null} */ (
    document.querySelector("[data-giftbox-cleanup-product-id]")
  );
  var giftboxProductId = parseInt(
    giftboxEl?.dataset?.giftboxCleanupProductId || "0",
    10,
  );

  var giftcardEl = /** @type {HTMLElement | null} */ (
    document.querySelector("[data-giftcard-cleanup-product-id]")
  );
  var giftcardProductId = parseInt(
    giftcardEl?.dataset?.giftcardCleanupProductId || "0",
    10,
  );

  return {
    giftboxProductId: giftboxProductId,
    giftcardProductId: giftcardProductId,
  };
}

/**
 * Scan cart items and return keys of orphaned upsells.
 * @param {any} cartData
 * @param {number} giftboxProductId
 * @param {number} giftcardProductId
 * @returns {string[]}
 */
function findOrphanedKeys(cartData, giftboxProductId, giftcardProductId) {
  var parentGroups = new Set();
  var parentTitles = new Set();
  var orphanedKeys = [];

  for (var i = 0; i < cartData.items.length; i++) {
    var ci = cartData.items[i];
    var pid = ci.product_id;
    if (pid !== giftboxProductId && pid !== giftcardProductId) {
      parentTitles.add(ci.product_title);
      var pg = ci.properties ? ci.properties["_upsell_group"] : null;
      if (pg) parentGroups.add(pg);
    }
  }

  for (var j = 0; j < cartData.items.length; j++) {
    var item = cartData.items[j];
    var itemGroup = item.properties ? item.properties["_upsell_group"] : null;

    if (giftboxProductId && item.product_id === giftboxProductId) {
      var giftWrapFor = item.properties
        ? item.properties["Gift wrap for"]
        : null;
      if (!giftWrapFor) continue;
      if (itemGroup) {
        if (!parentGroups.has(itemGroup)) orphanedKeys.push(item.key);
      } else {
        if (!parentTitles.has(giftWrapFor)) orphanedKeys.push(item.key);
      }
    }

    if (giftcardProductId && item.product_id === giftcardProductId) {
      var giftCardFor = item.properties
        ? item.properties["Gift card for"]
        : null;
      if (!giftCardFor) continue;
      if (itemGroup) {
        if (!parentGroups.has(itemGroup)) orphanedKeys.push(item.key);
      } else {
        if (!parentTitles.has(giftCardFor)) orphanedKeys.push(item.key);
      }
    }
  }

  return orphanedKeys;
}

/**
 * Remove orphaned upsell items via a single /cart/update.js call and refresh the cart UI.
 * @param {string[]} orphanedKeys
 * @returns {Promise<void>}
 */
async function removeOrphans(orphanedKeys) {
  var routes = /** @type {any} */ (window.Shopify)?.routes?.root || "/";

  /** @type {Record<string, number>} */
  var updates = {};
  for (var i = 0; i < orphanedKeys.length; i++) {
    var ok = orphanedKeys[i];
    if (ok) updates[ok] = 0;
  }

  await fetch(routes + "cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ updates: updates }),
  });

  var updatedRes = await fetch(routes + "cart.js");
  var updatedCart = await updatedRes.json();

  var drawer = /** @type {HTMLElement | null} */ (
    document.querySelector("cart-drawer")
  );
  var hadAutoOpen = drawer ? drawer.hasAttribute("auto-open") : false;
  if (drawer) drawer.removeAttribute("auto-open");

  document.dispatchEvent(
    new CartAddEvent(updatedCart, "cart-giftbox-cleanup", {
      itemCount: updatedCart.item_count,
      source: "cart-giftbox-cleanup",
    }),
  );

  if (hadAutoOpen && drawer) {
    requestAnimationFrame(function () {
      if (drawer) drawer.setAttribute("auto-open", "");
    });
  }
}

// MARCA | 14/05/2026 | Safety-net orphan cleanup on cart update events.
// With atomic batch removes this should rarely find anything, but it catches edge cases
// (e.g. network failure mid-operation, browser crash, manual URL manipulation).
document.addEventListener(ThemeEvents.cartUpdate, async function (event) {
  if (cleanupInProgress) return;

  var detail = /** @type {any} */ (event).detail;
  var source = detail?.data?.source;
  if (source === "cart-giftbox-cleanup") return;

  var ids = detectOrphans();
  if (!ids.giftboxProductId && !ids.giftcardProductId) return;

  var cartData;
  try {
    var r = await fetch("/cart.js");
    cartData = await r.json();
  } catch {
    return;
  }
  if (!cartData?.items) return;

  var orphanedKeys = findOrphanedKeys(
    cartData,
    ids.giftboxProductId,
    ids.giftcardProductId,
  );
  if (orphanedKeys.length === 0) return;

  cleanupInProgress = true;
  try {
    await removeOrphans(orphanedKeys);
  } catch {
    /* Silent fail – next cart event will retry */
  } finally {
    cleanupInProgress = false;
  }
});

// MARCA | 14/05/2026 | One-time page-load cleanup – catches orphans left by abnormal exits
// (browser crash, network failure, tab close mid-operation). Runs once on DOMContentLoaded.
document.addEventListener('DOMContentLoaded', async function () {
  var ids = detectOrphans();
  if (!ids.giftboxProductId && !ids.giftcardProductId) return;

  try {
    var res = await fetch('/cart.js');
    var cartData = await res.json();
    if (!cartData?.items) return;

    var orphanedKeys = findOrphanedKeys(cartData, ids.giftboxProductId, ids.giftcardProductId);
    if (orphanedKeys.length === 0) return;

    await removeOrphans(orphanedKeys);
  } catch {
    /* Silent – best-effort cleanup */
  }
});
